import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getTenants,
  getTenant,
  createTenant,
  updateTenant,
} from "@/api/endpoints/tenants";
import type { TenantCreatePayload, TenantUpdatePayload } from "@/types";

/**
 * Fetch the list of all tenants visible to the current user.
 * Admins see all tenants; regular users see only their own.
 *
 * @returns A TanStack Query result with an array of `Tenant` objects.
 */
export function useTenants() {
  return useQuery({
    queryKey: ["tenants"],
    queryFn: getTenants,
  });
}

/**
 * Fetch a single tenant by its string ID.
 * The query is disabled when `id` is falsy.
 *
 * @param id - The tenant's database ID (as a string).
 * @returns A TanStack Query result with the `Tenant` object.
 */
export function useTenant(id: string) {
  return useQuery({
    queryKey: ["tenants", id],
    queryFn: () => getTenant(id),
    enabled: !!id,
  });
}

/**
 * Mutation hook to create a new tenant.
 * On success, invalidates the `["tenants"]` query cache so the tenant
 * list is refreshed.
 *
 * @returns A TanStack Mutation that accepts a `TenantCreatePayload`.
 */
export function useCreateTenant() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: TenantCreatePayload) => createTenant(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tenants"] });
    },
  });
}

/**
 * Mutation hook to update an existing tenant's properties.
 * On success, invalidates the `["tenants"]` query cache.
 *
 * @returns A TanStack Mutation that accepts `{ id, payload }`.
 */
export function useUpdateTenant() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: TenantUpdatePayload }) =>
      updateTenant(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tenants"] });
    },
  });
}
